import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, CircularProgress, InputAdornment, Typography, } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { useSafeSistaAsync } from "@sista/library-stasi";
import { useForm } from "react-hook-form";
import { FormContainer, TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { IdmClientContext } from "@sista/idm-client";
import { DistaConfirmDialog, DistaMarkdownViewer } from "@sista/dista-ui";
import { SubdirectoryArrowRight } from "@mui/icons-material";
import { useSpravaTextuClient } from "../../util/useSpravaTextuClient";
import { applyVariantUrns, SelectVariantUrns } from "./SelectVariantUrns";
const iconSize = { width: "16px", height: "16px" };
export const SingleTextDataComponent = ({ canEdit, canTranslate, canView, languages, textData, tree, model, markdowChecked, onChange, onDelete, resetingFormOnSucessForNextText, }) => {
    const { t } = useTranslation("sprava-textu");
    const { loggedAccount } = useContext(IdmClientContext);
    const form = useForm({
        defaultValues: textData ?? null,
    });
    const spratex = useSpravaTextuClient();
    const [translateCall, translateState] = useSafeSistaAsync(spratex.translate);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [variantToDelete, setVariantToDelete] = useState(null);
    const testModel = useMemo(() => {
        if (model == null)
            return null;
        return model[0];
    }, [model]);
    const getTranslationForLanguage = useCallback((lang) => {
        if (lang === "cs") {
            return t("sprava-textu.singleTextData.translations.cs", "Čeština");
        }
        if (lang === "en") {
            return t("sprava-textu.singleTextData.translations.en", "English");
        }
        if (lang === "de") {
            return t("sprava-textu.singleTextData.translations.de", "Deutsch");
        }
        if (lang === "fr") {
            return t("sprava-textu.singleTextData.translations.fr", "Français");
        }
        if (lang === "es") {
            return t("sprava-textu.singleTextData.translations.es", "Español");
        }
        if (lang === "it") {
            return t("sprava-textu.singleTextData.translations.it", "Italiano");
        }
        if (lang === "ru") {
            return t("sprava-textu.singleTextData.translations.ru", "Русский");
        }
        if (lang === "pt") {
            return t("sprava-textu.singleTextData.translations.pt", "Português");
        }
        if (lang === "zh") {
            return t("sprava-textu.singleTextData.translations.zh", "中文");
        }
        if (lang === "ja") {
            return t("sprava-textu.singleTextData.translations.ja", "日本語");
        }
        if (lang === "ko") {
            return t("sprava-textu.singleTextData.translations.ko", "한국어");
        }
        return lang;
    }, [t]);
    useEffect(() => {
        if (textData == null)
            return form.reset();
        form.reset({
            validFrom: textData?.validFrom ?? null,
            translations: {
                cs: textData?.translations?.cs ?? null,
                en: textData?.translations?.en ?? null,
            },
            ai: textData?.ai ?? null,
            providerUrn: textData?.providerUrn ?? null,
            programUrn: textData?.programUrn ?? null,
            cfpUrn: textData?.cfpUrn ?? null,
            providerUrns: textData?.providerUrns ?? null,
            programUrns: textData?.programUrns ?? null,
            cfpUrns: textData?.cfpUrns ?? null,
            composedId: textData?.composedId ?? null,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [textData, form.reset]);
    const csText = form.watch("translations.cs");
    const enText = form.watch("translations.en");
    const handleTranslate = useCallback(async (lang, model) => {
        if (csText == null)
            return null;
        const result = await translateCall({
            lang: lang,
            model: model,
            value: csText,
        });
        if (result != null) {
            const langVal = ("translations." + lang);
            form.setValue(langVal, result.value);
            onChange(form.getValues());
            return result.value;
        }
        return null;
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.setValue, onChange, translateCall, csText]);
    /* const translateIfNeeded = useCallback(async () => {
      for (const lang of languages) {
        if (lang === "en") {
          if (
            csText != null &&
            testModel != null &&
            (enText == null || enText.trim().length == 0)
          ) {
            const result = await handleTranslate("en", testModel);
            return result;
          }
        }
      }
    }, [csText, languages, enText, testModel, handleTranslate]);
  
    useEffect(() => {
      if (textData == null) return;
      translateIfNeeded();
    }, [translateIfNeeded, textData]); */
    const handleChange = (data) => {
        onChange(data);
    };
    const onClickHandler = useCallback((e) => {
        if (enText == null || enText.trim().length == 0) {
            return handleTranslate("en", testModel);
        }
    }, [handleTranslate, testModel, enText]);
    useEffect(() => {
        if (resetingFormOnSucessForNextText) {
            form.reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetingFormOnSucessForNextText, form.reset]);
    const [showMarkdownPreview, setShowMarkdownPreview] = useState([]);
    const hasProviderUrn = (text) => text != null &&
        (text.providerUrn != null || (text.providerUrns?.length ?? 0) > 0);
    const isVariant = hasProviderUrn(textData);
    const isDisabled = useCallback((text, lang) => {
        if (loggedAccount.providerId !== "tacr" && !hasProviderUrn(text))
            return true;
        if (canEdit)
            return false;
        if (lang === "cs" && canTranslate)
            return true;
        if (lang !== "cs" && canTranslate)
            return false;
        if (canView)
            return true;
        return false;
    }, [canEdit, canTranslate, canView, loggedAccount.providerId]);
    return (_jsx(FormContainer, { formContext: form, children: _jsxs(Stack, { direction: "row", children: [isVariant && _jsx(SubdirectoryArrowRight, { sx: { mt: 1, mr: 1 } }), _jsxs(Stack, { direction: "column", spacing: markdowChecked ? 0 : 1, sx: { flexGrow: 1 }, children: [isVariant && (_jsx(Stack, { direction: "column", children: _jsx(SelectVariantUrns, { canEdit: canEdit, data: {
                                    providerUrns: textData.providerUrns ??
                                        (textData.providerUrn ? [textData.providerUrn] : []),
                                    programUrns: textData.programUrns ??
                                        (textData.programUrn ? [textData.programUrn] : []),
                                    cfpUrns: textData.cfpUrns ??
                                        (textData.cfpUrn ? [textData.cfpUrn] : []),
                                }, tree: tree, onChange: (data) => {
                                    //console.log("Selected variant data single component:", data);
                                    const updatedTextData = applyVariantUrns(form.getValues(), data);
                                    handleChange(updatedTextData);
                                } }) })), languages.map((lang) => (_jsx(Stack, { direction: "column", spacing: 0, children: (showMarkdownPreview?.includes(lang) ?? false) ? (_jsxs(_Fragment, { children: [_jsxs(Box, { component: "fieldset", sx: {
                                            border: "1px solid #ccc",
                                            borderRadius: 1,
                                            m: 0,
                                            p: 1,
                                        }, children: [_jsx("legend", { children: _jsx(Typography, { variant: "body1Xs", sx: { color: "#ccc" }, children: t("sprava-textu.singleTextData.translations." + lang, "Náhled pro " + lang) }) }), _jsx(DistaMarkdownViewer, { value: form.getValues(("translations." + lang)) ?? "" })] }), _jsxs(Stack, { direction: "row", spacing: 1, sx: {}, children: [_jsx(Box, { sx: { flexGrow: 1 } }), _jsx(Button, { variant: "text", size: "small", onClick: () => setShowMarkdownPreview(showMarkdownPreview.filter((l) => l !== lang)), children: t("sprava-textu.singleTextData.edit", "Upravit") })] })] })) : (_jsxs(_Fragment, { children: [_jsx(TextFieldElement, { name: "translations." + lang, label: t("sprava-textu.singleTextData.translations." + lang, getTranslationForLanguage(lang)), multiline: true, minRows: markdowChecked ? 2 : 1, onChange: (e) => {
                                            const updatedTextData = {
                                                ...form.getValues(),
                                                translations: {
                                                    ...form.getValues().translations,
                                                    [lang]: e.target.value,
                                                },
                                            };
                                            handleChange(updatedTextData);
                                        }, InputProps: {
                                            onClick: lang === "en" ? onClickHandler : null,
                                            endAdornment: translateState.pending && lang != "cs" ? (_jsxs(InputAdornment, { position: "start", children: [_jsx(CircularProgress, { sx: iconSize }), _jsx(Typography, { variant: "body2", sx: { ml: 1 }, children: t("sprava-textu.singleTextData.translating", "Překládám...") })] })) : null,
                                        }, disabled: isDisabled(textData, lang) }, lang), markdowChecked && (_jsxs(Stack, { direction: "row", spacing: 1, sx: {}, children: [_jsx(Box, { sx: { flexGrow: 1 } }), _jsx(Button, { variant: "text", size: "small", onClick: () => {
                                                    setShowMarkdownPreview(showMarkdownPreview
                                                        ? [...showMarkdownPreview, lang]
                                                        : [lang]);
                                                }, children: t("sprava-textu.singleTextData.showMarkdownPreview", "Náhled") })] }))] })) }, lang))), canEdit && isVariant && onDelete && (_jsxs(Stack, { direction: "row", sx: { mt: 1 }, children: [_jsx(Box, { sx: { flexGrow: 1 } }), _jsx(Button, { variant: "contained", color: "error", size: "small", onClick: () => {
                                        setVariantToDelete(textData);
                                        return setShowConfirmDelete(true);
                                    }, children: t("sprava-textu.singleTextData.deleteVariant", "Smazat variantu") })] })), showConfirmDelete && variantToDelete && (_jsx(DistaConfirmDialog, { open: showConfirmDelete, onClose: () => setShowConfirmDelete(false), okAction: {
                                type: "button",
                                children: t("sprava-textu.singleTextData.confirmDialog.ok", "Ano"),
                                onClick: () => {
                                    setShowConfirmDelete(false);
                                    onDelete(variantToDelete);
                                    setVariantToDelete(null);
                                },
                            }, cancelAction: {
                                type: "button",
                                children: t("sprava-textu.singleTextData.confirmDialog.cancel", "Zrušit"),
                                onClick: () => {
                                    setShowConfirmDelete(false);
                                    setVariantToDelete(null);
                                },
                            }, title: t("sprava-textu.singleTextData.deleteVariant", "Smazat variantu"), children: t("sprava-textu.singleTextData.deleteVariantConfirmation", "Opravdu chcete smazat tuto variantu?") }))] })] }) }));
};
